/**
 * One row in the customer map's structure browser.
 *
 * A row is the list-side twin of a marker, and the list-side twin of the
 * structures grid's row: the same status dot the grid paints, the name, the
 * site's description under it the way the grid's name cell carries it,
 * whichever extra details the reader configured,
 * and — last, where the eye lands after the identity — the alarms themselves,
 * named and coloured by severity, so an operator can triage from the list
 * without opening anything.
 *
 * Hovering lifts the matching marker, clicking opens the site, and the locate
 * action flies the map to it.
 */
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';

import BaseAlarmChip from '@sophocles/components/AlarmChip';
import UnitStatusDot, { unitStatusLabel } from '@sophocles/components/UnitStatusDot';
import { hasMaintenance } from '@sophocles/features/map/mapState';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import { collapseAlarmChips } from '@sophocles/views/map/mapAlarmChips';
import { fieldValue } from '@sophocles/views/map/mapFields';
import useMapAlarmChips from '@sophocles/views/map/useMapAlarmChips';
import AlarmChip from '@sophocles/views/sites/components/alarms/AlarmChip';

// How many named alarms a row shows before the rest collapse into one chip.
// The alarm band is one line, and beyond three chips a row stops being
// scannable anyway.
const MAX_ROW_ALARM_CHIPS = 3;

function SiteAlarms({ site, t, tAlarm, resolveAlarmChips }) {
  const { visible, hidden } = collapseAlarmChips(resolveAlarmChips(site.alarms), MAX_ROW_ALARM_CHIPS);
  if (visible.length === 0) return null;
  return (
    <>
      {visible.map(chip => (
        <AlarmChip
          key={chip.key}
          categoryKey={chip.categoryKey}
          label={chip.label}
          tAlarm={tAlarm}
          count={chip.count}
          severity={chip.severity}
        />
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.map(chip => chip.label).join(', ')}>
          <BaseAlarmChip
            color="neutral"
            label={t('map.moreAlarms', { count: hidden.length, defaultValue: '+{{count}} more' })}
          />
        </Tooltip>
      )}
    </>
  );
}

/**
 * @param {object} props
 * @param {object} props.item - a map site row.
 * @param {object} props.style - react-virtualized absolute positioning for the row.
 * @param {string} props.status - the row's unit-status token, on the grid's ladder.
 * @param {boolean} [props.isHovered] - the matching marker is hovered.
 * @param {boolean} [props.isSelected] - this row's site is the one open in the sidebar.
 * @param {(siteId: ?number) => void} props.onHover
 * @param {(item: object) => void} props.onSelect - open the row's site.
 * @param {(item: object) => void} props.onFind - fly the map to the row's site.
 * @param {string[]} props.shownFields - configured extra-detail field ids.
 * @param {Record<string, string>} props.labelsById - localized label per field id.
 * @param {boolean} [props.showGroupName] - show the owning group (off when the map is already filtered to one).
 */
export default function MapSiteListRow({
  item,
  style,
  status,
  isHovered = false,
  isSelected = false,
  onHover,
  onSelect,
  onFind,
  shownFields,
  labelsById,
  showGroupName = false,
}) {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const resolveAlarmChips = useMapAlarmChips(tAlarm);
  const groupName = showGroupName ? item.site_group_name : null;
  const showsMaintenance = hasMaintenance(item);

  const handleFind = event => {
    event.stopPropagation();
    onFind(item);
  };

  return (
    <Box
      style={style}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') onSelect(item);
      }}
      onMouseEnter={() => onHover(item.site_id ?? item.id)}
      onMouseLeave={() => onHover(null)}
      sx={{
        boxSizing: 'border-box',
        px: 1.5,
        py: 1,
        borderBottom: '1px solid var(--border-default)',
        borderLeft: '3px solid',
        borderLeftColor: isSelected ? 'primary.main' : 'transparent',
        cursor: 'pointer',
        bgcolor: isHovered || isSelected ? 'var(--surface-container-high)' : 'transparent',
        '&.Mui-focusVisible, &:focus-visible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UnitStatusDot status={status} title={unitStatusLabel(status, t)} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name || t('map.noName', 'No name')}
        </Typography>
        <Tooltip title={t('map.centerMapOnStructure', 'center map on this structure')}>
          <IconButton
            size="small"
            onClick={handleFind}
            aria-label={t('map.centerMapOnStructure', 'center map on this structure')}
          >
            <MyLocationIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>

      {item.description && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }} noWrap>
          {item.description}
        </Typography>
      )}

      {shownFields.map(fieldId => (
        <Typography
          key={fieldId}
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ lineHeight: 1.5 }}
          noWrap
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            {labelsById[fieldId]}:
          </Box>{' '}
          {fieldValue(fieldId, item) || t('map.none', 'None')}
        </Typography>
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
        <SiteAlarms site={item} t={t} tAlarm={tAlarm} resolveAlarmChips={resolveAlarmChips} />
        {showsMaintenance && (
          <Tooltip title={t('map.maintenanceOpen', 'Open maintenance log')}>
            <BuildOutlinedIcon fontSize="inherit" sx={{ color: 'var(--status-attention)' }} />
          </Tooltip>
        )}
        {groupName && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 'auto', pl: 0.5 }}>
            {groupName}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
